import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { Local } from 'src/shared/services/client/client';
import { RegionConfigService } from 'src/shared/services/region-config/region-config.service';

// Hand-written client for the /v1/integrator endpoints. They are deliberately excluded
// from the generated client (tag:client_exclude_endpoint) because they authenticate with
// an integrator API key rather than the signed-in staff member's token.

export interface IntegratorUserResponse {
  user_id: string;
  customer_number: string;
  email: string;
  first_name: string;
  last_name: string;
  post_code: string;
  phone_number: string;
  created_at: string;
}

export interface CreateIntegratorUserRequest {
  customer_number: string;
  first_name: string;
  last_name: string;
  email: string;
  post_code: string;
  phone_number: string;
  country_code: string;
}

export interface CreateIntegratorUserResponse {
  user_id: string;
}

export interface CreateIntegratorPetRequest {
  name: string;
  species: string;
  breed?: string;
  /** Exact Joii breed reference; takes priority over the fuzzy-matched `breed` text. */
  breed_doc_id?: string;
  gender?: string;
  date_of_birth: string;
  neutered?: boolean;
  weight?: { weight_value: number; unit: 'kg' | 'g' | 'lbs' };
  triage?: boolean;
  policy_number: string;
  policy_active?: boolean;
  policy_start_date?: string;
}

export interface IntegratorPetMutationResponse {
  pet_id: string;
  breed_matched: boolean;
  resolved_breed?: string;
  /** doc_id of the resolved breed — store it and send it as breed_doc_id later. */
  resolved_breed_doc_id?: string;
}

export interface IntegratorPetResponse {
  pet_id: string;
  name: string;
  species: string;
  breed?: string;
  breed_doc_id?: string;
  gender: string;
  date_of_birth: string;
  neutered?: boolean;
  weight?: { g: number; kg: number; lbs: number };
  policy_number?: string;
  customer_number?: string;
  policy_active: boolean;
  policy_start_date?: string;
  triage: boolean;
  created_at: string;
}

export interface ListIntegratorPetsResponse {
  pets: IntegratorPetResponse[];
}

/**
 * Route-scoped (see joii-sessions-routes providers). Holds the integrator API key in memory
 * for the lifetime of the joii-sessions section — no localStorage.
 */
@Injectable()
export class IntegratorApiService {
  private http = inject(HttpClient);
  private regionConfig = inject(RegionConfigService);

  readonly apiKey = signal('');

  setApiKey(key: string): void {
    this.apiKey.set(typeof key === 'string' ? key : String(key ?? ''));
  }

  getUserByCustomerNumber(apiKey: string, customerNumber: string): Observable<IntegratorUserResponse> {
    return this.http.get<IntegratorUserResponse>(
      `${this.baseUrl()}/v1/integrator/users/${encodeURIComponent(customerNumber)}/by-customer-number`,
      { headers: this.headers(apiKey) }
    );
  }

  createUser(apiKey: string, body: CreateIntegratorUserRequest): Observable<CreateIntegratorUserResponse> {
    return this.http.post<CreateIntegratorUserResponse>(`${this.baseUrl()}/v1/integrator/users`, body, {
      headers: this.headers(apiKey),
    });
  }

  createPet(
    apiKey: string,
    userDocId: string,
    body: CreateIntegratorPetRequest
  ): Observable<IntegratorPetMutationResponse> {
    return this.http.post<IntegratorPetMutationResponse>(
      `${this.baseUrl()}/v1/integrator/users/${encodeURIComponent(userDocId)}/pets`,
      body,
      { headers: this.headers(apiKey) }
    );
  }

  listPets(apiKey: string, userDocId: string): Observable<ListIntegratorPetsResponse> {
    return this.http.get<ListIntegratorPetsResponse>(
      `${this.baseUrl()}/v1/integrator/users/${encodeURIComponent(userDocId)}/pets`,
      { headers: this.headers(apiKey) }
    );
  }

  private baseUrl(): string {
    const config = this.regionConfig.getConfigForRegion();
    return config.api !== '' ? config.api : Local;
  }

  private headers(apiKey: string): Record<string, string> {
    return { Authorization: `Bearer ${apiKey}` };
  }
}
